import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Asterisk } from "lucide-react";
import { NAV_SECTIONS, INDEPENDENT_NOTICE, FAQ_LIST } from "../content/site";

export const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50" style={{ background: "rgba(247,244,238,0.88)", backdropFilter: "blur(14px)", borderBottom: "1px solid var(--line)" }}>
      <div className="text-center py-1.5 px-4 text-[11px] uppercase tracking-[0.2em] font-medium" style={{ background: "var(--ink)", color: "var(--paper)" }} data-testid="independence-banner">
        {INDEPENDENT_NOTICE}
      </div>
      <div className="max-w-6xl mx-auto px-5 flex items-center justify-between h-16">
        <Link to="/" data-testid="site-logo" className="flex items-center gap-2 no-underline">
          <Asterisk size={22} style={{ color: "var(--accent)" }} className="spin-slow" />
          <span className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>
            U.CENTER <em className="font-light">Reviewed</em>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-7" aria-label="Primary">
          {[
            { to: "/what-is-ucenter", label: "Explainers" },
            { to: "/compensation-plan", label: "Compensation" },
            { to: "/utopia-app", label: "Product" },
            { to: "/how-to-verify", label: "Diligence" },
            { to: "/faq/is-ucenter-a-bank", label: "FAQ" },
          ].map((l) => (
            <NavLink key={l.to} data-testid={`nav-${l.label.toLowerCase()}`} to={l.to}
              className="text-sm no-underline transition-colors"
              style={{ color: "var(--ink-muted)" }}
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/contact" data-testid="nav-correction" className="outline-btn outline-btn--accent no-underline !py-2 !px-4 text-xs">
            Correct my work
          </Link>
        </nav>
        <button data-testid="mobile-menu-toggle" className="md:hidden p-2" onClick={() => setOpen(!open)} aria-label="Toggle menu" style={{ color: "var(--ink)" }}>
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <nav className="md:hidden border-t px-5 py-6 space-y-6 max-h-[70vh] overflow-y-auto" style={{ borderColor: "var(--line)", background: "var(--paper)" }} aria-label="Mobile">
          {NAV_SECTIONS.map((s) => (
            <div key={s.label}>
              <p className="text-xs uppercase tracking-[0.2em] mb-2 font-medium" style={{ color: "var(--ink-muted)" }}>{s.label}</p>
              <div className="flex flex-col gap-2">
                {s.links.map((l) => (
                  <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-base no-underline" style={{ color: "var(--ink)" }}>{l.label}</Link>
                ))}
              </div>
            </div>
          ))}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] mb-2 font-medium" style={{ color: "var(--ink-muted)" }}>FAQ</p>
            <div className="flex flex-col gap-2">
              {FAQ_LIST.map((f) => (
                <Link key={f.slug} to={`/faq/${f.slug}`} onClick={() => setOpen(false)} className="text-base no-underline" style={{ color: "var(--ink)" }}>{f.q}</Link>
              ))}
            </div>
          </div>
        </nav>
      )}
    </header>
  );
};

export const SiteFooter = () => (
  <footer className="mt-24 border-t" style={{ borderColor: "var(--line)", background: "var(--card)" }} data-testid="site-footer">
    <div className="max-w-6xl mx-auto px-5 py-16 grid gap-12 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Asterisk size={20} style={{ color: "var(--accent)" }} />
          <span className="font-serif-display text-lg" style={{ color: "var(--ink)" }}>U.CENTER <em className="font-light">Reviewed</em></span>
        </div>
        <p className="text-sm leading-relaxed max-w-xs" style={{ color: "var(--ink-muted)" }}>
          {INDEPENDENT_NOTICE} Weeks of research, official sources, and an honest verdict, no referral links, nothing for sale.
        </p>
        <p className="text-xs mt-6" style={{ color: "var(--ink-muted)" }}>
          Nothing here is legal, financial, or investment advice. Read the <Link to="/risk" className="underline" style={{ color: "var(--accent)" }}>risk statement</Link>.
        </p>
      </div>
      {NAV_SECTIONS.slice(0, 3).map((s) => (
        <div key={s.label}>
          <p className="text-xs uppercase tracking-[0.2em] mb-4 font-medium" style={{ color: "var(--ink-muted)" }}>{s.label}</p>
          <ul className="space-y-2 p-0 m-0 list-none">
            {s.links.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-sm no-underline transition-colors hover:underline" style={{ color: "var(--ink)" }}>{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
    <div className="border-t py-6" style={{ borderColor: "var(--line)" }}>
      <div className="max-w-6xl mx-auto px-5 flex flex-wrap gap-x-8 gap-y-2 text-xs" style={{ color: "var(--ink-muted)" }}>
        <Link to="/sources" className="no-underline hover:underline" style={{ color: "var(--ink-muted)" }}>Sources</Link>
        <Link to="/risk" className="no-underline hover:underline" style={{ color: "var(--ink-muted)" }}>Risk & disclaimers</Link>
        <Link to="/about" className="no-underline hover:underline" style={{ color: "var(--ink-muted)" }}>About this site</Link>
        <Link to="/contact" className="no-underline hover:underline" style={{ color: "var(--ink-muted)" }}>Suggest a correction</Link>
        <a href="/llms.txt" className="no-underline hover:underline" style={{ color: "var(--ink-muted)" }}>llms.txt</a>
        <a href="/sitemap.xml" className="no-underline hover:underline" style={{ color: "var(--ink-muted)" }}>Sitemap</a>
        <span className="ml-auto">Last reviewed 7 September 2026</span>
      </div>
    </div>
  </footer>
);
